// src/components/common/NotificationList.tsx
import { format } from 'date-fns';
import { AlertCircle, Bell, CheckCircle2, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button, Skeleton, ScrollArea } from "@/components/ui";
import type { Notification } from '@/mocks/notifications';

const getNotificationIcon = (type: string) => {
    switch (type) {
        case 'success':
            return <CheckCircle2 className="text-green-500" />
        case 'warning':
            return <AlertCircle className="text-yellow-500" />
        case 'info':
            return <Info className="text-blue-500" />
        default:
            return <Bell className="text-gray-500" />
    }
}

type NotificationListProps = {
    notifications: Notification[];
    onViewAll: () => void;
    isLoading?: boolean;
};

export default function NotificationList({ notifications, onViewAll, isLoading = false }: NotificationListProps) {
    const { t } = useTranslation();

    if (isLoading) {
        return (
            <ScrollArea className="h-[400px]">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="px-4 py-3 flex gap-3">
                        <Skeleton className="h-10 w-10 rounded-full" />
                        <div className="flex-1 space-y-2">
                            <Skeleton className="h-4 w-3/4" />
                            <Skeleton className="h-3 w-1/2" />
                            <Skeleton className="h-3 w-1/4" />
                        </div>
                    </div>
                ))}
            </ScrollArea>
        );
    }

    return (
        <>
            <ScrollArea className="h-[400px]">
                {notifications.map((notification) => (
                    <div
                        key={notification.id}
                        className="px-4 py-3 flex gap-3 hover:bg-accent cursor-pointer"
                    >
                        <div >
                            {getNotificationIcon(notification.type)}
                        </div>
                        <div>
                            <p className="text-sm font-medium">
                                {notification.title}
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                {notification.message}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                                {format(notification.date, 'HH:mm')}
                            </p>
                        </div>
                    </div>
                ))}
            </ScrollArea>
            <div className="p-2 text-center">
                <Button variant="ghost" size="sm" className="w-full text-primary" onClick={onViewAll}>
                    {t("header.viewAllNotifications")}
                </Button>
            </div>
        </>
    );
} 